import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import {
  Activity, AlertTriangle, ArrowLeft, BadgeCheck, Bell, ChevronDown,
  CircleAlert, Eye, Flame, Globe, Lock, Newspaper, Radar, Scale,
  ShieldCheck, Smartphone, Sparkles, Star, TrendingUp, Volume2, Zap,
} from "lucide-react";
import { api } from "../../lib/api";
import {
  NABD_FONT_AR, NABD_FONT_DISPLAY, ensureNabdFonts, nabdVisitorId,
} from "../../lib/nabdTheme";
import InAppBrowserHint from "../../components/InAppBrowserHint";

// نبض السوق AI pricing in AED — converted to nominal USD (×0.27 — AED pegged
// ~3.67 to USD) for TikTok, whose USD-only benchmark aggregates cross-product
// reporting.
const NABD_PRICE_AED = 99;
const NABD_PRICE_AED_ORIGINAL = 199;
const NABD_USD = Math.round(NABD_PRICE_AED * 0.27 * 100) / 100;
const NABD_CONTENTS = {
  contents: [{
    content_id: "nabd_market_ai",
    content_type: "product",
    content_name: "نبض السوق AI",
    quantity: 1,
    price: NABD_USD,
  }],
  value: NABD_USD,
  currency: "USD",
};

// TikTok pixel helper — wraps ttq.track with the nabd content payload. Each
// browser call can share an event_id with the corresponding S2S Purchase
// from the backend webhook so TikTok dedupes correctly.
const ttFireNabd = (eventName, opts = {}) => {
  if (typeof window === "undefined" || !window.ttq?.track) return;
  try {
    window.ttq.track(
      eventName,
      NABD_CONTENTS,
      opts.eventId ? { event_id: opts.eventId } : undefined,
    );
  } catch (_) { /* analytics never breaks the funnel */ }
};
const newTtEid = (label) =>
  `nabd_${label}_${Date.now()}_${Math.random().toString(36).slice(2, 8)}`;

// Tracking helper — never throws, never blocks navigation.
const sendNabd = (event, props = {}) => {
  try {
    const payload = JSON.stringify({
      event, props, visitor_id: nabdVisitorId(), page: "landing-nabd",
    });
    const url = `${process.env.REACT_APP_BACKEND_URL}/api/nabd/track`;
    if (navigator.sendBeacon)
      navigator.sendBeacon(url, new Blob([payload], { type: "application/json" }));
    else api.post("/nabd/track", JSON.parse(payload));
  } catch (_) { /* analytics never breaks the funnel */ }
};

const FEATURES = [
  {
    icon: Activity,
    title: "نبض السوق اليومي",
    desc:
      "ملخّص ذكي لأهم ما يحدث: الأسهم الإماراتية، أسواق الخليج، النفط، الذهب، العملات، الكريبتو، والأخبار الاقتصادية. بدون تعقيد. فقط ما يهمّ المستثمر.",
  },
  {
    icon: Radar,
    title: "رادار الفرص والمخاطر",
    desc:
      "يرصد التحركات غير المعتادة: أصل بدأ يتحرك بقوة، خبر مؤثر، موجة اهتمام مفاجئة، أو ارتفاع في المخاطرة. تشوف الفرصة قبل ما تنتشر.",
  },
  {
    icon: Bell,
    title: "تنبيهات حسب اهتماماتك",
    desc:
      "اختر نمطك الاستثماري: محافظ، متوازن، أو هجومي — وحدّد الأصول اللي تتابعها. التنبيهات تُصمَّم لك أنت فقط.",
  },
  {
    icon: TrendingUp,
    title: "تحليل ذكي لكل أصل",
    desc:
      "ماذا يحدث الآن؟ ولماذا؟ خبر أم ضجيج؟ السيناريو المتفائل والسلبي، وأهم شيء يجب مراقبته — كل ذلك في صفحة واحدة.",
  },
];

const PERSONALITIES = [
  { label: "محافظ",   icon: ShieldCheck },
  { label: "متوازن",  icon: Scale },
  { label: "هجومي",   icon: Flame },
  { label: "أسهم",    icon: TrendingUp },
  { label: "ذهب",     icon: Star },
  { label: "نفط",     icon: Activity },
  { label: "كريبتو",  icon: Zap },
];

const ATTENTION_LEVELS = [
  { level: "هادئ",            color: "#5B6573",  desc: "لا يوجد ما يستحق الانتباه الآن." },
  { level: "يستحق المتابعة",   color: "#F4B842",  desc: "حركة طبيعية لكنها تستدعي الملاحظة." },
  { level: "تحرّك غير عادي",   color: "#00D4A8",  desc: "تغيّر واضح في السلوك السعري أو الحجم." },
  { level: "مخاطرة مرتفعة",    color: "#EF6B6B",  desc: "ضغط بيع أو خبر سلبي مؤثر." },
  { level: "ضجيج زائد",        color: "#9C7CFF",  desc: "ترند متأخر — انتبه قبل أي قرار." },
];

const TESTIMONIALS = [
  {
    name: "أبو فيصل",
    location: "دبي",
    rating: 5,
    text:
      "أتابع الأسواق منذ سنوات، وكنت ضايع بين عشرات القنوات والقروبات. نبض السوق ركّز لي الصورة في خمس دقائق يوميًا.",
  },
  {
    name: "محمد الزعابي",
    location: "أبوظبي",
    rating: 5,
    text:
      "أعجبني فلتر الشركات الشرعي ورادار الضجيج. الفرق بين الترند المتأخر والحركة الحقيقية صار واضح، وفّر علي قرارات اندفاعية.",
  },
  {
    name: "أبو نواف",
    location: "الشارقة",
    rating: 5,
    text:
      "ما هو تطبيق تداول، وهذا اللي شدّني. أداة فهم. أستخدمه قبل ما أفتح وسيطي وأقرر بنفسي. بسعر الإطلاق صفقة.",
  },
];

const FAQ = [
  {
    q: "هل نبض السوق يعطيني توصيات شراء وبيع؟",
    a: "لا. نبض السوق أداة معلومات وتحليل ومتابعة. يساعدك على الفهم قبل القرار، لكنه لا يقدم توصيات استثمارية مباشرة ولا يضمن أرباحًا.",
  },
  {
    q: "ما الذي يميزه عن تطبيقات التداول والشارت؟",
    a: "تطبيقات التداول تعرض البيانات. نبض السوق يعطيك الوضوح: ما الذي تغيّر، لماذا، وهل يستحق الانتباه. هو طبقة ذكية فوق السوق، وليس بديلًا عن وسيطك.",
  },
  {
    q: "ما الأسواق التي يغطيها؟",
    a: "أسواق الإمارات (ADX و DFM)، الأسهم الخليجية، الأسهم الأمريكية، الذهب، النفط، العملات، والكريبتو — مع أخبار اقتصادية مؤثرة.",
  },
  {
    q: "هل يوفر فلترة شرعية للأسهم؟",
    a: "نعم. يوفر تصنيفًا مبدئيًا (نقي / مختلط / يحتاج مراجعة) لمساعدة المستثمر الذي يهتم بالتوافق الشرعي.",
  },
  {
    q: "هل التطبيق متاح على iPhone و Android؟",
    a: "نعم. بعد الدفع تحصل على رابط مباشر لتنزيل النسخة Android، ورابط لنسخة iOS من App Store.",
  },
  {
    q: "هل هناك اشتراك شهري؟",
    a: "لا. في مرحلة الإطلاق، الدفع مرة واحدة فقط مقابل النسخة الحالية. لا اشتراك شهري ولا رسوم متكررة.",
  },
  {
    q: "هل هناك ضمان استرداد؟",
    a: "نعم. ضمان استرداد كامل خلال 7 أيام إذا لم يناسبك التطبيق. بدون أسئلة محرجة.",
  },
];

const SectionTitle = ({ kicker, children, center = true, className = "" }) => (
  <div className={`mb-12 ${center ? "text-center" : ""} ${className}`}>
    {kicker && (
      <div
        className="text-[10.5px] tracking-[0.32em] mb-3 font-bold"
        style={{ color: "#F4B842" }}
      >
        {kicker}
      </div>
    )}
    <h2
      className="font-extrabold text-[26px] sm:text-[34px] leading-[1.35] text-white"
      style={{ fontFamily: NABD_FONT_DISPLAY }}
    >
      {children}
    </h2>
  </div>
);

export default function LandingNabd() {
  const navigate = useNavigate();
  const [openFaq, setOpenFaq] = useState(-1);

  useEffect(() => {
    ensureNabdFonts();
    document.title = "نبض السوق AI — لوحة قيادة المستثمر الخليجي";
    sendNabd("page_view");
    // TikTok ViewContent — fires once per landing visit. PageView itself is
    // already fired globally by index.html on every route.
    ttFireNabd("ViewContent", { eventId: newTtEid("viewcontent") });
    const start = Date.now();
    const onLeave = () => {
      const secs = Math.round((Date.now() - start) / 1000);
      if (secs > 2)
        navigator.sendBeacon &&
          navigator.sendBeacon(
            `${process.env.REACT_APP_BACKEND_URL}/api/nabd/track`,
            new Blob(
              [JSON.stringify({ event: "time_on_site", seconds: secs, visitor_id: nabdVisitorId() })],
              { type: "application/json" }
            )
          );
    };
    window.addEventListener("beforeunload", onLeave);
    return () => window.removeEventListener("beforeunload", onLeave);
  }, []);

  const goCheckout = (label) => {
    sendNabd("cta_click", { label });
    // TikTok AddToCart — every CTA click on the landing counts as cart intent.
    ttFireNabd("AddToCart", { eventId: newTtEid(`addtocart-${label}`) });
    navigate("/nabd/checkout");
  };

  return (
    <div
      dir="rtl"
      lang="ar"
      className="min-h-screen bg-[#06090F] text-[#E8ECEF] selection:bg-[#F4B842] selection:text-[#06090F]"
      style={{ fontFamily: NABD_FONT_AR }}
      data-testid="nabd-landing-root"
    >
      <InAppBrowserHint />
      {/* Scarcity bar — static (no live counter) */}
      <div
        className="relative z-40 text-center px-4 py-2 text-[12px] font-bold text-[#06090F] flex items-center justify-center gap-2"
        style={{ background: "linear-gradient(90deg,#FFD27A 0%,#F4B842 50%,#D89B23 100%)" }}
        data-testid="nabd-scarcity-banner"
      >
        <Flame className="w-3.5 h-3.5" />
        <span>سعر الإطلاق ٩٩ درهم بدلاً من ١٩٩ — للمستخدمين الأوائل فقط</span>
      </div>

      {/* Nav */}
      <nav className="sticky top-0 z-30 backdrop-blur-md border-b border-white/[0.04] bg-[#06090F]/85">
        <div className="max-w-6xl mx-auto px-5 py-3.5 flex items-center justify-between">
          <div className="flex items-center gap-2.5">
            <div className="w-9 h-9 rounded-xl flex items-center justify-center border" style={{ background: "linear-gradient(180deg,#0E141C,#040608)", borderColor: "rgba(244,184,66,0.40)" }}>
              <Activity className="w-4 h-4 text-[#F4B842]" strokeWidth={2.4} />
            </div>
            <div className="leading-tight">
              <div className="font-bold text-[14px] text-white" style={{ fontFamily: NABD_FONT_DISPLAY }}>نبض السوق <span className="text-[#F4B842]">AI</span></div>
              <div className="text-[10px] text-[#6B7480]">للمستثمر الخليجي</div>
            </div>
          </div>
          <button
            onClick={() => goCheckout("nav-cta")}
            data-testid="nabd-nav-cta"
            className="text-[12.5px] font-bold px-4 py-2 rounded-full transition-colors border"
            style={{ background: "rgba(244,184,66,0.10)", color: "#F4B842", borderColor: "rgba(244,184,66,0.45)" }}
          >
            احصل عليه
          </button>
        </div>
      </nav>

      {/* HERO */}
      <header className="relative overflow-hidden">
        <div
          aria-hidden="true"
          className="absolute inset-0 opacity-[0.55]"
          style={{
            background:
              "radial-gradient(ellipse 60% 40% at 50% 0%, rgba(244,184,66,0.18) 0%, transparent 60%), radial-gradient(ellipse 40% 30% at 80% 30%, rgba(0,212,168,0.10) 0%, transparent 65%)",
          }}
        />
        <div className="relative max-w-5xl mx-auto px-5 pt-16 sm:pt-24 pb-12 text-center">
          <div
            className="inline-flex items-center gap-2 text-[10.5px] tracking-[0.28em] font-bold mb-6 rounded-full px-4 py-1.5 border"
            style={{ background: "rgba(0,212,168,0.10)", color: "#00D4A8", borderColor: "rgba(0,212,168,0.30)" }}
          >
            <span className="w-1.5 h-1.5 rounded-full bg-[#00D4A8] animate-pulse" />
            <span>السوق يتحرّك الآن</span>
          </div>
          <h1
            className="font-extrabold text-[32px] sm:text-[48px] lg:text-[60px] leading-[1.18] text-white max-w-3xl mx-auto"
            style={{ fontFamily: NABD_FONT_DISPLAY, fontWeight: 900 }}
            data-testid="nabd-hero-title"
          >
            لا تدخل السوق وأنت <span className="text-[#F4B842]">آخر من يفهم</span> ما يحدث.
          </h1>
          <p className="mt-6 text-[15.5px] sm:text-[17px] text-[#9AA5B1] max-w-2xl mx-auto leading-[1.95]">
            سهم يتحرّك. الذهب يصعد. النفط يضغط على السوق. خبر صغير يغيّر المزاج. والأغلبية لا تنتبه إلا متأخرًا.
            <br/>
            <span className="text-white font-semibold">نبض السوق AI</span> لوحة قيادة ذكية ترصد التحركات، تشرح ما يحدث، وتنبّهك قبل القرار.
          </p>

          <div className="mt-9 flex flex-col sm:flex-row items-center justify-center gap-3">
            <button
              onClick={() => goCheckout("hero-primary")}
              data-testid="nabd-hero-cta"
              className="group inline-flex items-center justify-center gap-2 px-7 py-4 rounded-full font-extrabold text-[15px] text-[#06090F] shadow-[0_18px_40px_-12px_rgba(244,184,66,0.45)] transition-transform active:scale-[0.98]"
              style={{ background: "linear-gradient(180deg,#FFD27A,#F4B842 70%,#D89B23)" }}
            >
              احصل على نبض السوق AI
              <ArrowLeft className="w-4 h-4 group-hover:-translate-x-0.5 transition-transform" />
            </button>
            <div className="text-[12.5px] text-[#6B7480] flex items-center gap-2">
              <Lock className="w-3 h-3" />
              دفع آمن · ضمان 7 أيام
            </div>
          </div>

          {/* Highlighted delivery strip under the hero CTA */}
          <div
            className="mt-5 inline-flex items-center justify-center gap-3 rounded-2xl px-4 py-2.5 border mx-auto"
            style={{
              background: "linear-gradient(90deg, rgba(0,212,168,0.10) 0%, rgba(244,184,66,0.14) 100%)",
              borderColor: "rgba(244,184,66,0.40)",
            }}
            data-testid="nabd-hero-delivery-strip"
          >
            <div className="text-right leading-tight">
              <div className="text-[10px] tracking-[0.18em] text-[#00D4A8] font-bold mb-0.5">أحصل على التطبيق الآن</div>
              <div className="text-[12px] text-white font-bold">رابط تحميل مباشر</div>
            </div>
            <div className="w-px h-7 bg-white/15" />
            <div className="flex items-center gap-1.5">
              <span className="inline-flex items-center text-[11px] font-bold text-[#F4B842] bg-[#06090F]/70 border border-[#F4B842]/30 rounded-full px-2.5 py-1">Android</span>
              <span className="inline-flex items-center text-[11px] font-bold text-[#E8ECEF] bg-[#06090F]/70 border border-white/15 rounded-full px-2.5 py-1">iOS</span>
            </div>
          </div>

          {/* Trust chips */}
          <div className="mt-8 flex flex-wrap items-center justify-center gap-2" data-testid="nabd-trust-chips">
            {[
              { icon: BadgeCheck, label: "١٤٩ درهم — مرة واحدة" },
              { icon: ShieldCheck, label: "ضمان استرداد ٧ أيام" },
              { icon: Lock, label: "دفع Stripe آمن" },
              { icon: Smartphone, label: "Android + iOS" },
            ].map((c, i) => (
              <div key={i} className="inline-flex items-center gap-1.5 text-[11.5px] text-[#9AA5B1] bg-[#0B1018] border border-white/[0.06] rounded-full px-3 py-1.5">
                <c.icon className="w-3 h-3 text-[#F4B842]" />
                {c.label}
              </div>
            ))}
          </div>
        </div>

        {/* Mockup preview */}
        <div className="relative max-w-3xl mx-auto px-5 pb-16">
          <div
            className="relative rounded-[18px] overflow-hidden border p-5 sm:p-6"
            style={{
              background: "linear-gradient(180deg,#0E141C 0%,#080C12 100%)",
              borderColor: "rgba(244,184,66,0.22)",
              boxShadow: "0 30px 80px -30px rgba(0,0,0,0.7)",
            }}
            data-testid="nabd-hero-mockup"
          >
            <div className="flex items-center justify-between mb-4">
              <div className="flex items-center gap-2">
                <span className="w-2 h-2 rounded-full bg-[#00D4A8] animate-pulse" />
                <span className="text-[11px] text-[#9AA5B1] tracking-[0.18em] font-bold">نبض الآن</span>
              </div>
              <div className="text-[10.5px] text-[#6B7480]">آخر تحديث منذ دقيقة</div>
            </div>
            <div className="grid grid-cols-2 sm:grid-cols-4 gap-2.5 mb-3">
              {[
                { name: "تاسي", val: "+0.74%", up: true },
                { name: "ذهب", val: "+0.41%", up: true },
                { name: "نفط", val: "-1.12%", up: false },
                { name: "BTC", val: "+2.16%", up: true },
              ].map((m, i) => (
                <div key={i} className="rounded-xl p-3 border" style={{ background: "#040608", borderColor: "rgba(255,255,255,0.05)" }}>
                  <div className="text-[10.5px] text-[#6B7480] mb-0.5">{m.name}</div>
                  <div className={`font-bold text-[14px]`} style={{ color: m.up ? "#00D4A8" : "#EF6B6B" }}>{m.val}</div>
                </div>
              ))}
            </div>
            <div className="space-y-2">
              <div className="flex items-start gap-3 rounded-xl p-3 border" style={{ background: "rgba(0,212,168,0.06)", borderColor: "rgba(0,212,168,0.20)" }}>
                <Radar className="w-4 h-4 mt-0.5 text-[#00D4A8] shrink-0" />
                <div className="text-[12.5px] text-[#E8ECEF] leading-snug">
                  تحرّك غير عادي في قطاع البنوك — حجم تداول مضاعف. <span className="text-[#00D4A8] font-bold">يستحق المتابعة.</span>
                </div>
              </div>
              <div className="flex items-start gap-3 rounded-xl p-3 border" style={{ background: "rgba(244,184,66,0.06)", borderColor: "rgba(244,184,66,0.22)" }}>
                <Newspaper className="w-4 h-4 mt-0.5 text-[#F4B842] shrink-0" />
                <div className="text-[12.5px] text-[#E8ECEF] leading-snug">
                  إعلان شركة مدرجة عن عقد بقيمة ٢٫٤ مليار — <span className="text-[#F4B842] font-bold">تأثير محتمل إيجابي قصير المدى.</span>
                </div>
              </div>
              <div className="flex items-start gap-3 rounded-xl p-3 border" style={{ background: "rgba(239,107,107,0.05)", borderColor: "rgba(239,107,107,0.18)" }}>
                <Volume2 className="w-4 h-4 mt-0.5 text-[#EF6B6B] shrink-0" />
                <div className="text-[12.5px] text-[#E8ECEF] leading-snug">
                  ضجيج إعلامي حول عملة محددة — <span className="text-[#EF6B6B] font-bold">انتبه: ترند متأخر.</span>
                </div>
              </div>
            </div>
            {/* Soft glow */}
            <div className="pointer-events-none absolute -top-10 right-1/3 w-40 h-40 rounded-full opacity-30 blur-3xl" style={{ background: "#F4B842" }} />
          </div>
        </div>
      </header>

      {/* Disclaimer strip */}
      <section className="py-6 border-y border-white/[0.04] bg-[#040608]">
        <div className="max-w-3xl mx-auto px-5 flex flex-col sm:flex-row items-center justify-center gap-2.5 text-center">
          <CircleAlert className="w-4 h-4 text-[#F4B842] shrink-0" />
          <div className="text-[13px] text-[#9AA5B1]">
            <span className="text-white font-bold">ليس توصيات شراء وبيع.</span> هو يعطيك ما هو أهم —
            <span className="text-[#F4B842] font-bold"> فهم أسرع، رؤية أوضح، تنبيهات أذكى.</span>
          </div>
        </div>
      </section>

      {/* PROBLEM */}
      <section className="py-18 sm:py-24 border-b border-white/[0.04]">
        <div className="max-w-3xl mx-auto px-5 text-center">
          <SectionTitle kicker="المشكلة">المعلومات كثيرة، السوق سريع، والقرار صعب.</SectionTitle>
          <p className="text-[15px] text-[#9AA5B1] leading-[2] mb-8">
            المشكلة ليست نقص المعلومات. المشكلة أن المعلومات كثيرة، سريعة، متضاربة، ومليئة بالضجيج.
          </p>
          <div className="grid sm:grid-cols-2 gap-3 max-w-xl mx-auto text-right">
            {[
              "خبر مهم يضيع بين عشرات العناوين",
              "حركة طبيعية تُقدَّم على أنها فرصة",
              "ترند متأخر يجذب الناس للاندفاع",
              "مخاطرة مرتفعة لا أحد ينبهك إليها",
            ].map((t, i) => (
              <div key={i} className="flex items-start gap-3 bg-[#0B1018] border border-white/[0.05] rounded-xl px-4 py-3 text-[13.5px] text-[#E8ECEF]">
                <AlertTriangle className="w-4 h-4 text-[#F4B842] mt-0.5 shrink-0" />
                <span>{t}</span>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* FEATURES */}
      <section className="py-20 sm:py-24 border-b border-white/[0.04] relative">
        <div className="max-w-5xl mx-auto px-5">
          <SectionTitle kicker="ماذا يفعل نبض السوق؟">أربع قدرات في تطبيق واحد.</SectionTitle>
          <div className="grid sm:grid-cols-2 gap-4">
            {FEATURES.map((f, i) => (
              <div
                key={i}
                className="relative rounded-2xl p-6 border transition-transform hover:-translate-y-0.5"
                style={{ background: "linear-gradient(180deg,#0E141C,#080C12)", borderColor: "rgba(244,184,66,0.18)" }}
                data-testid={`nabd-feature-${i}`}
              >
                <div className="w-11 h-11 rounded-xl flex items-center justify-center mb-4 border" style={{ background: "rgba(244,184,66,0.10)", borderColor: "rgba(244,184,66,0.35)" }}>
                  <f.icon className="w-5 h-5 text-[#F4B842]" strokeWidth={2} />
                </div>
                <h3 className="font-bold text-[17px] text-white mb-2" style={{ fontFamily: NABD_FONT_DISPLAY }}>{f.title}</h3>
                <p className="text-[13.5px] text-[#9AA5B1] leading-[1.95]">{f.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* WHY DIFFERENT */}
      <section className="py-20 sm:py-24 border-b border-white/[0.04]">
        <div className="max-w-3xl mx-auto px-5">
          <SectionTitle kicker="لماذا نبض السوق مختلف؟">
            معظم التطبيقات تعطيك بيانات أكثر. نحن نعطيك <span className="text-[#F4B842]">وضوحًا أكثر.</span>
          </SectionTitle>
          <p className="text-[14px] text-[#9AA5B1] leading-[2] text-center mb-9">
            هناك تطبيقات تداول، تطبيقات شارتات، ومنصات تعرض مؤشرات كثيرة. لكن المستثمر لا يحتاج المزيد من الأرقام — يحتاج أن يعرف:
          </p>
          <div className="grid sm:grid-cols-2 gap-3">
            {[
              { q: "ما الذي تغيّر؟", icon: Eye },
              { q: "لماذا يتحرك السوق؟", icon: Radar },
              { q: "هل الحركة مهمة أم ضجيج؟", icon: Volume2 },
              { q: "ما الذي يستحق المتابعة؟", icon: Sparkles },
              { q: "أين توجد المخاطرة؟", icon: AlertTriangle },
              { q: "ما الذي يجب ألا يتجاهله اليوم؟", icon: Bell },
            ].map((row, i) => (
              <div key={i} className="flex items-center gap-3 rounded-xl px-4 py-3 border" style={{ background: "#0B1018", borderColor: "rgba(255,255,255,0.05)" }}>
                <row.icon className="w-4 h-4 text-[#F4B842]" />
                <span className="text-[14px] text-[#E8ECEF]">{row.q}</span>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* INVESTOR PERSONALITY */}
      <section className="py-20 sm:py-24 border-b border-white/[0.04]">
        <div className="max-w-3xl mx-auto px-5">
          <SectionTitle kicker="تنبيهات حسب شخصيتك">اختر النمط — نضبط لك التطبيق.</SectionTitle>
          <p className="text-[14px] text-[#9AA5B1] leading-[2] text-center mb-8">
            المستثمر المحافظ لا يريد نفس تنبيهات المستثمر الهجومي. اختر نمطك واهتماماتك، وتصبح التنبيهات أكثر صلة بك.
          </p>
          <div className="flex flex-wrap items-center justify-center gap-2.5">
            {PERSONALITIES.map((p, i) => (
              <div
                key={i}
                className="inline-flex items-center gap-2 text-[13px] font-bold px-4 py-2.5 rounded-full border transition-colors"
                style={{ background: "#0B1018", color: "#E8ECEF", borderColor: "rgba(244,184,66,0.30)" }}
                data-testid={`nabd-personality-${i}`}
              >
                <p.icon className="w-3.5 h-3.5 text-[#F4B842]" />
                {p.label}
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ATTENTION SCORE */}
      <section className="py-20 sm:py-24 border-b border-white/[0.04]" data-testid="nabd-attention-score">
        <div className="max-w-3xl mx-auto px-5">
          <SectionTitle kicker="درجة الانتباه">بدل «اشترِ» و«بِع» — نعطيك وضوحًا.</SectionTitle>
          <p className="text-[14px] text-[#9AA5B1] leading-[2] text-center mb-9">
            لكل أصل ولكل لحظة، يعرض نبض السوق درجة انتباه واضحة تساعدك تتخذ قرارك بنفسك بوعي.
          </p>
          <div className="space-y-2.5">
            {ATTENTION_LEVELS.map((l, i) => (
              <div key={i} className="flex items-center gap-4 rounded-xl px-4 py-3.5 border" style={{ background: "#0B1018", borderColor: "rgba(255,255,255,0.05)" }}>
                <span className="w-2.5 h-2.5 rounded-full shrink-0" style={{ background: l.color, boxShadow: `0 0 12px ${l.color}80` }} />
                <div className="flex-1 flex flex-col sm:flex-row sm:items-baseline gap-1 sm:gap-3">
                  <span className="font-bold text-[14.5px] text-white">{l.level}</span>
                  <span className="text-[12.5px] text-[#9AA5B1]">{l.desc}</span>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* SMART READING + شرعية */}
      <section className="py-20 sm:py-24 border-b border-white/[0.04]">
        <div className="max-w-5xl mx-auto px-5 grid md:grid-cols-2 gap-5">
          <div className="rounded-2xl p-6 sm:p-7 border" style={{ background: "linear-gradient(180deg,#0E141C,#080C12)", borderColor: "rgba(244,184,66,0.22)" }}>
            <Newspaper className="w-6 h-6 text-[#F4B842] mb-3" />
            <h3 className="font-bold text-[18px] text-white mb-2" style={{ fontFamily: NABD_FONT_DISPLAY }}>قراءة ذكية لإعلانات الشركات</h3>
            <p className="text-[13.5px] text-[#9AA5B1] leading-[2]">
              عند صدور إعلان مهم، يلخّصه نبض السوق بلغة واضحة: ما الإعلان؟ إيجابي أم سلبي؟ ما التأثير المحتمل؟ هل يتعلق بالأرباح، التوزيعات، العقود، الديون، أو التوسعات؟
            </p>
          </div>
          <div className="rounded-2xl p-6 sm:p-7 border" style={{ background: "linear-gradient(180deg,#0E141C,#080C12)", borderColor: "rgba(0,212,168,0.30)" }}>
            <BadgeCheck className="w-6 h-6 text-[#00D4A8] mb-3" />
            <h3 className="font-bold text-[18px] text-white mb-2" style={{ fontFamily: NABD_FONT_DISPLAY }}>فلترة شرعية للمستثمر الخليجي</h3>
            <p className="text-[13.5px] text-[#9AA5B1] leading-[2] mb-3">
              تصنيف مبدئي يساعدك على فرز الأسهم حسب التوافق الشرعي:
            </p>
            <div className="flex flex-wrap gap-2">
              <span className="text-[11.5px] font-bold px-2.5 py-1 rounded-full border" style={{ background: "rgba(0,212,168,0.10)", color: "#00D4A8", borderColor: "rgba(0,212,168,0.30)" }}>نقي</span>
              <span className="text-[11.5px] font-bold px-2.5 py-1 rounded-full border" style={{ background: "rgba(244,184,66,0.10)", color: "#F4B842", borderColor: "rgba(244,184,66,0.30)" }}>مختلط</span>
              <span className="text-[11.5px] font-bold px-2.5 py-1 rounded-full border" style={{ background: "rgba(155,124,255,0.08)", color: "#9C7CFF", borderColor: "rgba(155,124,255,0.30)" }}>يحتاج مراجعة</span>
            </div>
          </div>
        </div>
      </section>

      {/* FOR WHOM */}
      <section className="py-20 sm:py-24 border-b border-white/[0.04]">
        <div className="max-w-3xl mx-auto px-5">
          <SectionTitle kicker="مناسب لمن؟">نبض السوق مناسب لك إذا كنت…</SectionTitle>
          <div className="grid sm:grid-cols-2 gap-3">
            {[
              "تستثمر في الأسواق الإماراتية والخليجية",
              "تتابع الذهب أو النفط",
              "تهتم بالكريبتو وتريد فهمًا واعيًا قبل القرار",
              "تريد فهم السوق بدون قراءة عشرات المصادر",
              "تريد أداة ترصد الفرص والمخاطر بوضوح",
              "تبحث عن أداة فهم — لا تطبيق تداول",
            ].map((t, i) => (
              <div key={i} className="flex items-start gap-3 bg-[#0B1018] border border-white/[0.05] rounded-xl px-4 py-3.5 text-[13.5px] text-[#E8ECEF]">
                <BadgeCheck className="w-4 h-4 text-[#00D4A8] mt-0.5 shrink-0" />
                <span>{t}</span>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* TESTIMONIALS */}
      <section className="py-20 sm:py-24 border-b border-white/[0.04]" data-testid="nabd-testimonials">
        <div className="max-w-5xl mx-auto px-5">
          <SectionTitle kicker="آراء المستثمرين الأوائل">قالها مستثمرون مثلك.</SectionTitle>
          <div className="grid sm:grid-cols-3 gap-4">
            {TESTIMONIALS.map((t, i) => (
              <div
                key={i}
                className="rounded-2xl p-5 sm:p-6 border"
                style={{ background: "linear-gradient(180deg,#0E141C,#080C12)", borderColor: "rgba(244,184,66,0.18)" }}
                data-testid={`nabd-testimonial-${i}`}
              >
                <div className="flex items-center gap-0.5 mb-3">
                  {Array.from({ length: t.rating }).map((_, k) => (
                    <Star key={k} className="w-3.5 h-3.5 text-[#F4B842]" fill="#F4B842" />
                  ))}
                </div>
                <p className="text-[13.5px] text-[#E8ECEF] leading-[1.95] mb-4">«{t.text}»</p>
                <div className="flex items-center justify-between pt-3 border-t border-white/[0.06]">
                  <div>
                    <div className="text-[13px] font-bold text-white">{t.name}</div>
                    <div className="text-[11px] text-[#6B7480]">{t.location}</div>
                  </div>
                </div>
              </div>
            ))}
          </div>
          <p className="mt-7 text-center text-[11.5px] text-[#404853]">
            * أسماء مستعارة لحماية الخصوصية — مراجعات من مستثمرين فعليين خلال مرحلة الإطلاق المغلقة.
          </p>
        </div>
      </section>

      {/* WHAT YOU GET */}
      <section className="py-20 sm:py-24 border-b border-white/[0.04]">
        <div className="max-w-3xl mx-auto px-5">
          <SectionTitle kicker="ماذا تحصل عليه؟">كل ما يحتاجه المستثمر الواعي.</SectionTitle>
          <div className="space-y-2.5">
            {[
              "دخول كامل إلى تطبيق نبض السوق AI",
              "ملخصات سوق ذكية يومية",
              "رادار تحركات غير معتادة",
              "تنبيهات مخصّصة حسب اهتماماتك",
              "تحليل مبسّط لكل أصل",
              "مؤشرات انتباه ومخاطر واضحة",
              "تصنيف توافق شرعي مبدئي",
              "تحديثات أساسية مجانية للنسخة الحالية",
            ].map((t, i) => (
              <div key={i} className="flex items-center gap-3 rounded-xl px-4 py-3 border" style={{ background: "#0B1018", borderColor: "rgba(255,255,255,0.05)" }}>
                <div className="w-6 h-6 rounded-full flex items-center justify-center shrink-0" style={{ background: "rgba(0,212,168,0.15)" }}>
                  <BadgeCheck className="w-3.5 h-3.5 text-[#00D4A8]" strokeWidth={2.5} />
                </div>
                <span className="text-[14px] text-[#E8ECEF]">{t}</span>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* PRICING */}
      <section className="py-20 sm:py-24 border-b border-white/[0.04] relative" id="pricing">
        <div
          aria-hidden="true"
          className="absolute inset-0 opacity-[0.25]"
          style={{ background: "radial-gradient(ellipse 50% 50% at 50% 50%, rgba(244,184,66,0.18) 0%, transparent 65%)" }}
        />
        <div className="relative max-w-md mx-auto px-5 text-center">
          <SectionTitle kicker="سعر الإطلاق">للمستخدمين الأوائل فقط.</SectionTitle>
          <div
            className="rounded-3xl p-7 sm:p-9 border relative overflow-hidden"
            style={{
              background: "linear-gradient(180deg,#0E141C 0%,#040608 100%)",
              borderColor: "rgba(244,184,66,0.45)",
              boxShadow: "0 30px 80px -25px rgba(244,184,66,0.30)",
            }}
            data-testid="nabd-pricing-card"
          >
            <div className="absolute top-0 left-0 right-0 h-1" style={{ background: "linear-gradient(90deg,transparent, #F4B842, transparent)" }} />
            <div className="text-[11px] tracking-[0.32em] text-[#F4B842] font-bold mb-4">نبض السوق AI</div>
            <div className="flex items-baseline justify-center gap-3 mb-1">
              <span className="text-[14px] text-[#6B7480] line-through">١٩٩</span>
              <span className="text-[58px] sm:text-[72px] font-extrabold text-white leading-none" style={{ fontFamily: NABD_FONT_DISPLAY }}>٩٩</span>
              <span className="text-[14px] text-[#F4B842] font-bold">درهم</span>
            </div>
            <div className="text-[12.5px] text-[#9AA5B1] mb-7">دفعة واحدة · بدون اشتراك شهري في مرحلة الإطلاق</div>
            <button
              onClick={() => goCheckout("pricing-primary")}
              data-testid="nabd-pricing-cta"
              className="w-full inline-flex items-center justify-center gap-2 px-6 py-4 rounded-full font-extrabold text-[15px] text-[#06090F] transition-transform active:scale-[0.98] shadow-[0_18px_40px_-12px_rgba(244,184,66,0.50)]"
              style={{ background: "linear-gradient(180deg,#FFD27A,#F4B842 70%,#D89B23)" }}
            >
              ابدأ الآن
              <ArrowLeft className="w-4 h-4" />
            </button>

            {/* Highlighted delivery strip under the pricing CTA */}
            <div
              className="mt-4 rounded-2xl p-3 flex items-center justify-center gap-3 border"
              style={{
                background: "linear-gradient(90deg, rgba(0,212,168,0.10) 0%, rgba(244,184,66,0.14) 100%)",
                borderColor: "rgba(244,184,66,0.40)",
              }}
              data-testid="nabd-pricing-delivery-strip"
            >
              <div className="text-right leading-tight">
                <div className="text-[10px] tracking-[0.18em] text-[#00D4A8] font-bold mb-0.5">أحصل على التطبيق الآن</div>
                <div className="text-[12px] text-white font-bold">رابط تحميل مباشر</div>
              </div>
              <div className="w-px h-7 bg-white/15" />
              <div className="flex items-center gap-1.5">
                <span className="inline-flex items-center text-[11px] font-bold text-[#F4B842] bg-[#06090F]/70 border border-[#F4B842]/30 rounded-full px-2.5 py-1">Android</span>
                <span className="inline-flex items-center text-[11px] font-bold text-[#E8ECEF] bg-[#06090F]/70 border border-white/15 rounded-full px-2.5 py-1">iOS</span>
              </div>
            </div>
            <div className="mt-4 flex flex-wrap items-center justify-center gap-2 text-[11px] text-[#6B7480]">
              <span className="inline-flex items-center gap-1"><Lock className="w-3 h-3" /> Stripe</span>
              <span>·</span>
              <span className="inline-flex items-center gap-1"><BadgeCheck className="w-3 h-3" /> Apple Pay</span>
              <span>·</span>
              <span>Visa · Mastercard</span>
            </div>
          </div>

          {/* Guarantee */}
          <div
            className="mt-6 rounded-2xl p-5 border flex items-center gap-4 text-right"
            style={{ background: "rgba(0,212,168,0.05)", borderColor: "rgba(0,212,168,0.25)" }}
            data-testid="nabd-guarantee"
          >
            <div className="w-12 h-12 rounded-full flex items-center justify-center shrink-0" style={{ background: "rgba(0,212,168,0.12)", border: "1px solid rgba(0,212,168,0.30)" }}>
              <ShieldCheck className="w-6 h-6 text-[#00D4A8]" />
            </div>
            <div>
              <div className="text-[10.5px] tracking-[0.22em] text-[#00D4A8] font-bold mb-0.5">ضمان ٧ أيام</div>
              <p className="text-[12.5px] text-[#E8ECEF] leading-[1.85]">جربه ٧ أيام، وإذا ما عجبك نرجّع لك مبلغك كاملًا — بدون أسئلة محرجة.</p>
            </div>
          </div>
        </div>
      </section>

      {/* APP DELIVERY PREVIEW */}
      <section className="py-16 sm:py-20 border-b border-white/[0.04]">
        <div className="max-w-3xl mx-auto px-5 text-center">
          <div className="text-[10.5px] tracking-[0.32em] text-[#6B7480] font-bold mb-4">يصلك بعد الدفع مباشرة</div>
          <h3 className="font-bold text-[20px] text-white mb-7" style={{ fontFamily: NABD_FONT_DISPLAY }}>تطبيق على Android و iOS — وصول فوري.</h3>
          <div className="flex flex-col sm:flex-row items-center justify-center gap-3">
            <div className="inline-flex items-center gap-3 px-5 py-3 rounded-xl border" style={{ background: "#0B1018", borderColor: "rgba(255,255,255,0.10)" }}>
              <Smartphone className="w-5 h-5 text-[#F4B842]" />
              <div className="text-right">
                <div className="text-[10px] text-[#6B7480]">تنزيل مباشر</div>
                <div className="text-[13px] font-bold text-white">Android · APK</div>
              </div>
            </div>
            <div className="inline-flex items-center gap-3 px-5 py-3 rounded-xl border" style={{ background: "#0B1018", borderColor: "rgba(255,255,255,0.10)" }}>
              <Globe className="w-5 h-5 text-[#F4B842]" />
              <div className="text-right">
                <div className="text-[10px] text-[#6B7480]">App Store</div>
                <div className="text-[13px] font-bold text-white">iPhone · iPad</div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* FAQ */}
      <section className="py-20 sm:py-24 border-b border-white/[0.04]" data-testid="nabd-faq">
        <div className="max-w-2xl mx-auto px-5">
          <SectionTitle kicker="أسئلة شائعة">سؤال بسيط؟ هنا الإجابة.</SectionTitle>
          <div className="space-y-2.5">
            {FAQ.map((row, i) => {
              const open = openFaq === i;
              return (
                <div key={i} className="rounded-xl border overflow-hidden" style={{ background: "#0B1018", borderColor: "rgba(255,255,255,0.05)" }}>
                  <button
                    onClick={() => { setOpenFaq(open ? -1 : i); if (!open) sendNabd("faq_open", { idx: i }); }}
                    data-testid={`nabd-faq-${i}`}
                    className="w-full text-right p-4 flex items-center justify-between gap-3"
                  >
                    <span className="text-[14px] font-bold text-white">{row.q}</span>
                    <ChevronDown className={`w-4 h-4 text-[#F4B842] transition-transform shrink-0 ${open ? "rotate-180" : ""}`} />
                  </button>
                  {open && (
                    <div className="px-4 pb-4 text-[13.5px] text-[#9AA5B1] leading-[2]">{row.a}</div>
                  )}
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* FINAL CTA */}
      <section className="py-20 sm:py-24">
        <div className="max-w-2xl mx-auto px-5 text-center">
          <h2
            className="font-extrabold text-[28px] sm:text-[36px] leading-[1.35] text-white mb-4"
            style={{ fontFamily: NABD_FONT_DISPLAY }}
          >
            ادخل السوق وأنت ترى الصورة <span className="text-[#F4B842]">أوضح.</span>
          </h2>
          <p className="text-[14.5px] text-[#9AA5B1] leading-[2] mb-8">
            احصل على نبض السوق AI الآن، وابدأ بفهم ما يحدث قبل أن يصل الخبر إلى الجميع.
          </p>
          <button
            onClick={() => goCheckout("final-cta")}
            data-testid="nabd-final-cta"
            className="inline-flex items-center justify-center gap-2 px-8 py-4 rounded-full font-extrabold text-[15.5px] text-[#06090F] transition-transform active:scale-[0.98] shadow-[0_18px_40px_-12px_rgba(244,184,66,0.50)]"
            style={{ background: "linear-gradient(180deg,#FFD27A,#F4B842 70%,#D89B23)" }}
          >
            احصل على نبض السوق AI — ١٤٩ درهم
            <ArrowLeft className="w-4 h-4" />
          </button>
          <div className="mt-3 text-[11.5px] text-[#6B7480]">دفع مرة واحدة · ضمان ٧ أيام · Android + iOS</div>
        </div>
      </section>

      {/* FOOTER */}
      <footer className="py-10 border-t border-white/[0.05] text-center px-5 bg-[#040608]">
        <p className="text-[11.5px] text-[#404853] max-w-2xl mx-auto leading-relaxed">
          <span className="text-[#9AA5B1] font-bold">نبض السوق AI</span> أداة معلومات وتحليل ومتابعة.
          لا يقدّم توصيات استثمارية مباشرة، ولا يضمن أرباحًا، ولا يغني عن قرارك الشخصي أو استشارة مختص مرخّص.
        </p>
      </footer>

      {/* MOBILE STICKY CTA — keeps the launch-price CTA permanently visible
          on phones. Mirrors the pattern used on Reply Rescue + 7days. */}
      <div
        className="sm:hidden fixed bottom-0 inset-x-0 z-40 bg-[#0E1116]/95 backdrop-blur border-t border-[#F4B842]/15 p-3 flex items-center gap-3"
        data-testid="nabd-sticky-cta-bar"
      >
        <div className="text-[11px] text-[#A89570] leading-tight flex-shrink-0">
          <div className="font-bold text-[#F0E9D6]">نبض السوق AI</div>
          <div className="tabular-nums"><span className="line-through text-[#6B7480] mr-1">١٩٩</span>٩٩ درهم · سعر الإطلاق</div>
        </div>
        <button
          onClick={() => goCheckout("sticky_cta")}
          data-testid="nabd-sticky-cta"
          className="ml-auto bg-gradient-to-r from-[#FFD27A] to-[#F4B842] text-[#06090F] font-extrabold text-sm px-5 py-3 rounded-full min-h-[48px] flex items-center justify-center shadow-[0_8px_24px_-8px_rgba(244,184,66,0.6)] active:scale-[0.97] transition-transform"
        >
          احصل عليه الآن
        </button>
      </div>
      <div className="sm:hidden h-20" aria-hidden />
    </div>
  );
}
